function buildHomepageEventParams({
  project = '',
  city = '',
  userId = '',
  source = '',
  ...eventParams
}) {
  return {
    project,
    city,
    user_id: userId == null ? '' : userId,
    source,
    ...eventParams,
    platform: 'Web',
  }
}

function trackHomepageEvent(eventName, params) {
  if (typeof window === 'undefined') return

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params)
    }
  } catch (error) {}

  try {
    if (window._paq && typeof window._paq.push === 'function') {
      const event = [
        'trackEvent',
        'Homepage',
        eventName,
        JSON.stringify(params),
      ]
      if (Number.isFinite(params.position)) {
        event.push(params.position)
      }
      window._paq.push(event)
    }
  } catch (error) {}
}

const HOMEPAGE_EVENT_CONFIG = {
  page_exposure: { eventName: 'homepage_exposure' },
  banner_exposure: {
    eventName: 'homepage_event_banner_exposure',
    parameterName: 'image_name',
    itemProperty: 'name',
  },
  banner_click: {
    eventName: 'homepage_event_banner_click',
    parameterName: 'image_name',
    itemProperty: 'name',
  },
  shortcut_exposure: {
    eventName: 'homepage_event_shortcut_exposure',
    parameterName: 'shortcut_id',
    itemProperty: 'id',
  },
  shortcut_click: {
    eventName: 'homepage_event_shortcut_click',
    parameterName: 'shortcut_id',
    itemProperty: 'id',
  },
  content_block_exposure: {
    eventName: 'homepage_event_content_block_exposure',
    parameterName: 'content_block_name',
    itemProperty: 'name',
  },
  content_block_click: {
    eventName: 'homepage_event_content_block_click',
    parameterName: 'content_block_name',
    itemProperty: 'name',
  },
}

function createHomepageTrackingEvent(type, context, item = {}, position) {
  const config = HOMEPAGE_EVENT_CONFIG[type]
  if (!config) return null

  const eventParams = config.parameterName
    ? { [config.parameterName]: item[config.itemProperty], position }
    : {}

  return {
    eventName: config.eventName,
    params: buildHomepageEventParams({ ...context, ...eventParams }),
  }
}

function isElementVisible(element, threshold) {
  if (!element || typeof element.getBoundingClientRect !== 'function') {
    return false
  }

  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return false

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  const visibleWidth = Math.max(
    0,
    Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0),
  )
  const visibleHeight = Math.max(
    0,
    Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
  )

  return (visibleWidth * visibleHeight) / (rect.width * rect.height) >= threshold
}

function createOnceExposureObserver(onExpose, { threshold = 0.5 } = {}) {
  const seenKeys = new Set()
  const metadata = new WeakMap()
  const observedElements = new WeakSet()
  const IntersectionObserverClass =
    typeof window !== 'undefined' && window.IntersectionObserver

  const expose = (element) => {
    const item = metadata.get(element)
    if (!item || seenKeys.has(item.key)) return

    seenKeys.add(item.key)
    onExpose(item.getEvent())
  }

  const observer = IntersectionObserverClass
    ? new IntersectionObserverClass(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < threshold) return
            if (!observedElements.has(entry.target)) return
            expose(entry.target)
            observer.unobserve(entry.target)
            observedElements.delete(entry.target)
          })
        },
        { threshold },
      )
    : null

  return {
    observe(element, key, getEvent) {
      if (!element || seenKeys.has(key)) return

      metadata.set(element, { key, getEvent })
      if (observer) {
        if (!observedElements.has(element)) {
          observedElements.add(element)
          observer.observe(element)
        }
      } else if (isElementVisible(element, threshold)) {
        expose(element)
      }
    },
    disconnect() {
      if (observer) observer.disconnect()
    },
  }
}

export {
  buildHomepageEventParams,
  createHomepageTrackingEvent,
  createOnceExposureObserver,
  trackHomepageEvent,
}
